<template>
    <div class="d-flex flex-column flex-grow-1">
        <div class="container-fluid p-0 login d-flex align-items-center flex-grow-1">
            <div class="w-100 py-3">
                <BAlert v-if="sessionNotice" :model-value="true" variant="warning" class="mx-auto"
                        style="max-width:600px;">
                    {{ sessionNotice === 'expired'
                        ? t('Your session has expired. Please sign in again.')
                        : t('Please sign in to continue.') }}
                </BAlert>
                <BCard class="mx-auto bg-transparent border-0" style="max-width:600px;">
                    <BCardTitle class="text-center">{{ t('Sign in') }}</BCardTitle>
                    <BCardBody class="d-flex flex-column gap-3">
                        <BForm @submit.prevent="login" class="d-flex flex-column gap-3">
                            <BTabs pills content-class="mt-3">
                                <BTab :title="t('Using your Connect account')" active>
                                    <div class="d-flex flex-column gap-3">
                                        <BFormGroup>
                                            <BInputGroup>
                                                <template #prepend>
                                                    <BInputGroupText>
                                                        <i class="bi bi-person-fill"></i>
                                                    </BInputGroupText>
                                                </template>
                                                <BFormInput :placeholder="t('E-mail address')" v-model="username"></BFormInput>
                                            </BInputGroup>
                                        </BFormGroup>
                                        <BFormGroup>
                                            <BInputGroup>
                                                <template #prepend>
                                                    <BInputGroupText>
                                                        <i class="bi bi-lock-fill"></i>
                                                    </BInputGroupText>
                                                </template>
                                                <BFormInput placeholder="******" type="password" v-model="password"></BFormInput>
                                            </BInputGroup>
                                        </BFormGroup>
                                    </div>
                                </BTab>
                                <BTab :title="t('Using your Active Directory account')">
                                    <div class="d-flex flex-column gap-3">
                                        <BFormGroup>
                                            <BInputGroup>
                                                <template #prepend>
                                                    <BInputGroupText>
                                                        <i class="bi bi-person-fill"></i>
                                                    </BInputGroupText>
                                                </template>
                                                <BFormInput :placeholder="t('Active Directory username')" v-model="username"></BFormInput>
                                            </BInputGroup>
                                        </BFormGroup>
                                        <BFormGroup>
                                            <BInputGroup>
                                                <template #prepend>
                                                    <BInputGroupText>
                                                        <i class="bi bi-lock-fill"></i>
                                                    </BInputGroupText>
                                                </template>
                                                <BFormInput placeholder="******" type="password" v-model="password"></BFormInput>
                                            </BInputGroup>
                                        </BFormGroup>
                                    </div>
                                </BTab>
                            </BTabs>
                            <BFormGroup>
                                <button type="submit" class="btn btn-primary btn-block">{{ t('Sign in') }}</button>
                            </BFormGroup>
                        </BForm>
                        <div class="text-center">
                            <BLink @click="showRegisterModal = true">{{ t("Don't have an account yet? Register here") }}</BLink>
                            <BLink class="ms-4" @click.prevent="requestPasswordReset()">{{
                                    t('Forgot your password?')
                                }}
                            </BLink>
                        </div>
                    </BCardBody>
                </BCard>
            </div>
        </div>
        <BModal v-model="showRegisterModal" :title="t('Register')" :ok-title="t('Send')" @ok.prevent="register()" size="lg">
            <Register v-model="user" ref="componentRegister"
                      @registrationSuccessful="showRegisterModal = false"></Register>
        </BModal>
        <BModal v-model="showResetPasswordModal" :title="t('Reset your password')" :ok-title="t('Save')"
                @ok.prevent="resetPassword" size="lg">
            <BForm ref="form-resetPassword">
                <BFormGroup :label="t('Password')" label-cols="3">
                    <BFormInput type="password" v-model="reset_password_data.password" required/>
                </BFormGroup>
                <BFormGroup :label="t('Confirm password')" label-cols="3">
                    <BFormInput type="password" v-model="reset_password_data.password_confirm" required/>
                </BFormGroup>
            </BForm>
        </BModal>
        <AxiosErrorInterceptor></AxiosErrorInterceptor>
    </div>
</template>

<script setup>
import LoginService from "../services/LoginService";
import i18n from "../i18n/i18n";
import Register from "../components/Register.vue";
import { useI18n } from 'vue-i18n';
import AxiosErrorInterceptor from "../components/snippets/AxiosErrorInterceptor.vue";
import {takeReturnTo} from "../services/Api";
import {
    BAlert,
    BCard, BCardBody,
    BCardTitle,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupText, BLink, BModal, BRow, BTab,
    BTabs
} from "bootstrap-vue-next";
import {ref, onMounted} from 'vue';
import {useRoute} from 'vue-router';

const { t } = useI18n();

const route = useRoute();

const username = ref('');
const password = ref('');
const user = ref({});
const reset_password_data = ref({});
const showRegisterModal = ref(false);
const showResetPasswordModal = ref(false);
const componentRegister = ref(null);

// The router uses hash history, so the reason we ended up here is in the real
// query string rather than in the route's: `expired` is set by Api.js after a
// 401, `redirect` by CakePHP when a protected page was opened directly.
const sessionNotice = ref((() => {
    const query = new URLSearchParams(window.location.search);
    if (query.has('expired')) {
        return 'expired';
    }

    return query.has('redirect') ? 'signInRequired' : null;
})());

function login() {
    LoginService.login(username.value, password.value).then((response) => {
        if (response.data.result === 'ok') {
            const returnTo = takeReturnTo();
            window.location.href = returnTo ? `/${returnTo}` : '/#/dashboard';
        } else if (response.data.result === 'invalid-credentials') {
            alert(i18n.global.t('Invalid credentials'));
        } else {
            alert('Unknown response.');
        }
    }).catch((error) => {
        if (error.response.data.message !== undefined) {
            alert(error.response.data.message);
        } else {
            alert(error);
        }
    });
}

function register() {
    componentRegister.value.submit();
}

function requestPasswordReset() {
    let email_address = prompt(i18n.global.t('Type in your e-mail-address:'));
    if (email_address) {
        LoginService.requestPasswordReset(email_address).then(r => {
            switch (r.data) {
                case 'user_not_found':
                    alert(i18n.global.t('We could not find a user account for this e-mail address.'));
                    break;
                case 'user_inactive':
                    alert(i18n.global.t('Your user account has not been activated yet. You will be notified when your account is ready.'));
                    break;
                case 'user_is_ldap':
                    alert(i18n.global.t('Your user account has been set up with your corporate credentials. Please use these to log in.'));
                    break;
                case 'email_sent':
                    alert(i18n.global.t('You will receive an e-mail message with further instructions to reset your password.'));
                    break;
            }
        });
    }
}

function resetPassword() {
    LoginService.resetPassword(reset_password_data.value).then(r => {
        if (r.data === 'ok') {
            alert(i18n.global.t('Your password has been reset.'));
            showResetPasswordModal.value = false;
        }
    });
}

onMounted(() => {
    if (route.query.mode && route.query.mode === 'resetPassword') {
        reset_password_data.value.token = route.query.token;
        showResetPasswordModal.value = true;
    }
});
</script>

<style scoped lang="scss">
@import "../scss/variables";
</style>
